import { useMemo, useState } from 'react';
import { CalendarPlus, X } from 'lucide-react';
import type { AbsenceListItem } from '@ip/shared-types';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  ABSENCE_TYPE_META, STATUS_CHIP, absenceStatusLabel, absenceTypeLabel, formatDateRange, formatDays,
} from '@/lib/hr';
import { t } from '@/i18n';

const COLLAPSED_COUNT = 6;

interface MyRequestsListProps {
  absences: AbsenceListItem[];
  onCancel: (id: string) => Promise<void>;
}

/** "Moje wnioski" — status-chipped history with cancel for pending requests. */
export function MyRequestsList({ absences, onCancel }: MyRequestsListProps) {
  const [showAll, setShowAll] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const sorted = useMemo(
    () => [...absences].sort((a, b) => (a.dateFrom < b.dateFrom ? 1 : -1)),
    [absences],
  );
  const visible = showAll ? sorted : sorted.slice(0, COLLAPSED_COUNT);

  const handleCancel = async (id: string) => {
    setBusyId(id);
    try {
      await onCancel(id);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t('hr.myRequests.title')}</CardTitle>
        {sorted.length > 0 && (
          <span className="text-muted-foreground text-xs tabular-nums">{sorted.length}</span>
        )}
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <div className="text-muted-foreground flex flex-col items-center gap-2 py-6 text-center text-sm">
            <CalendarPlus className="size-8 opacity-40" />
            <p className="max-w-56">{t('hr.myRequests.empty')}</p>
          </div>
        ) : (
          <ul className="divide-y">
            {visible.map((absence) => {
              const meta = ABSENCE_TYPE_META[absence.type];
              return (
                <li key={absence.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  <span className={cn('size-2 shrink-0 rounded-full', meta.dot)} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{absenceTypeLabel(absence.type)}</p>
                    <p className="text-muted-foreground text-xs">
                      {formatDateRange(absence.dateFrom, absence.dateTo)}
                      {absence.days > 0 && <> · {formatDays(absence.days)}</>}
                    </p>
                    {absence.status === 'rejected' && absence.decisionNote && (
                      <p className="text-muted-foreground truncate text-xs italic" title={absence.decisionNote}>
                        {t('hr.myRequests.decisionNote', { note: absence.decisionNote })}
                      </p>
                    )}
                  </div>
                  <Badge variant="outline" className={cn('shrink-0 font-normal', STATUS_CHIP[absence.status])}>
                    {absenceStatusLabel(absence.status)}
                  </Badge>
                  {absence.status === 'pending' && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive size-7 shrink-0"
                          aria-label={t('hr.myRequests.cancel')}
                          disabled={busyId === absence.id}
                        >
                          <X className="size-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('hr.myRequests.cancelTitle')}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('hr.myRequests.cancelBody', {
                              type: absenceTypeLabel(absence.type),
                              range: formatDateRange(absence.dateFrom, absence.dateTo),
                            })}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('hr.myRequests.cancelKeep')}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleCancel(absence.id)}>
                            {t('hr.myRequests.cancelConfirm')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {sorted.length > COLLAPSED_COUNT && (
          <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={() => setShowAll((v) => !v)}>
            {showAll ? t('hr.myRequests.showLess') : t('hr.myRequests.showAll', { count: sorted.length })}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
